import {
  APP_INITIALIZER,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';

import { CustomCurrencyMaskConfig, fetchBuildDetails } from './app/app.module';
import { environment } from './environments/environment';
import { DatePipe } from '@angular/common';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { CURRENCY_MASK_CONFIG, CurrencyMaskModule } from 'ng2-currency-mask';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorsInterceptor } from './app/core/interceptors/errors.interceptor';
import { HttpInterceptorService } from './app/core/services/http-interceptor.service';
import { provideTranslateHttpLoader, TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideTranslations } from './app/core/i18n/translations';
import { CookieService } from 'ngx-cookie-service';
import { CurrencyPipe } from './app/shared/pipes/currency.pipe';
import { BuildDetailsHttpService } from './app/core/services/build-details.http.service';
import { GoogleMapsLoaderService } from './app/core/services/google-maps-loader.service';
import { provideStaleBuildReload } from './app/core/errors/stale-build-reload';
import { bootstrapApplication } from '@angular/platform-browser';
import { DEFAULT_BRAND_LOGO_URL } from './app/core/branding/environment-logo';
import { applyEnvironmentFavicon } from './app/core/branding/environment-favicon';
import { paintBootShellBrand } from './app/core/layout/boot-shell';
import { provideTanStackQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { withDevtools } from '@tanstack/angular-query-experimental/devtools';
import { provideWetaxiDashboardApiApi } from './app/generated/v6/wetaxi-dashboard-api.client';
import { AppRoutingModule } from './app/app-routing.module';
import { MaterialModule } from './app/core/material/material.module';
import { provideAnimations } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { ClipboardModule } from 'ngx-clipboard';
import { GoogleMapsModule } from '@angular/google-maps';
import { AppComponent } from './app/app.component';
import * as packageJson from '../package.json';
import moment from 'moment-timezone';
import { ROME_TZ } from './app/core/constants/timezone';
import { initSentry, provideSentry } from './app/core/errors/sentry';

// All moment-based dates across the app display in Italian time (CET/CEST),
// regardless of the operator's browser timezone.
moment.tz.setDefault(ROME_TZ);

// The boot shell's one piece that cannot be written into index.html: the mark differs per
// environment, and that file is the same on all of them. Painted here rather than from
// `AppComponent`, which does not run until bootstrap has finished — and bootstrap waits on the
// Google Maps script and the build-details call, neither of which the logo has anything to do with.
paintBootShellBrand(DEFAULT_BRAND_LOGO_URL);

// Alongside it, and for the same reason: the tab is on screen before the app is, and stays
// there once the sidebar that would otherwise name the environment is collapsed. Production
// keeps the real mark.
void applyEnvironmentFavicon(environment.name);

// Before bootstrap, so an error thrown on the way up is still reported. A no-op on the
// local-only environments, which ship an empty DSN.
initSentry();

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      AppRoutingModule,
      MaterialModule,
      ToastrModule.forRoot(),
      ReactiveFormsModule,
      FormsModule,
      DragDropModule,
      CurrencyMaskModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useClass: TranslateHttpLoader,
        },
      }),
      ClipboardModule,
      GoogleMapsModule
    ),
    DatePipe,
    // A deploy retires the previous build's lazy chunks; tabs left open across it keep
    // asking for them and fail the dynamic import. Reload once instead of dead-ending.
    provideStaleBuildReload(),
    // Reports what `provideStaleBuildReload()`'s handler passes on, and traces navigations.
    provideSentry(),
    provideHttpClient(withInterceptorsFromDi()),
    provideNativeDateAdapter(),
    { provide: CURRENCY_MASK_CONFIG, useValue: CustomCurrencyMaskConfig },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorsInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpInterceptorService,
      multi: true,
    },
    // The version query busts long-lived browser caches: the i18n files keep stable
    // URLs across deploys, so without it a cached copy can outlive the keys it lacks.
    ...provideTranslateHttpLoader({ prefix: 'assets/i18n/', suffix: `.json?v=${packageJson.version}` }),
    // Holds bootstrap until that loader has answered: every screen seeds its labels from
    // `translate.instant(SCOPE)`, which answers with the key itself until the catalogue lands.
    provideTranslations(),
    { provide: LOCALE_ID, useValue: 'it' },
    CookieService,
    CurrencyPipe,
    {
      provide: APP_INITIALIZER,
      useFactory: fetchBuildDetails,
      deps: [BuildDetailsHttpService],
      multi: true,
    },
    // Hold bootstrap until the async Google Maps script is ready: components across the
    // app construct `google.maps.*` objects in their init hooks and crash on a hard
    // reload otherwise ("google is not defined").
    provideAppInitializer(() =>
      inject(GoogleMapsLoaderService)
        .whenReady()
        .then(() => undefined)
    ),
    { provide: MAT_SELECT_CONFIG, useValue: { panelWidth: null } },
    provideAnimations(),
    provideZonelessChangeDetection(),
    // wetaxi_dashboard_api v6 (DRT supervisor, cells, requests) — the generated client
    // appends `/v6/...`, so it takes the admin host as its base (see drt-rides.api.ts).
    provideWetaxiDashboardApiApi({ baseUrl: environment.admin_base_url }),
    provideTanStackQuery(
      new QueryClient({
        defaultOptions: {
          queries: {
            // These endpoints answer 200 with `status: 'error'`, so `errors.interceptor` never
            // sees a failure and a retry just repeats a call that cannot start working. Stating
            // it once here stops the next consumer deriving it again by hand. Refetch-on-window-
            // focus stays at the library default: with real staleTime tiers it only fires for
            // queries that are actually stale.
            retry: false,
          },
        },
      }),
      withDevtools()
    ),
  ],
}).catch((err) => console.error(err));
